<template>
  <div class="chat-page">
    <!-- ── Conversation sidebar ──────────────────────────────────────── -->
    <div class="chat-sidebar" :class="{ 'chat-sidebar--hidden': mobileShowMessages }">
      <div class="chat-sidebar__header">
        <h5 class="mb-0">Messages</h5>
        <div class="d-flex gap-2">
          <button class="btn-icon" title="New direct message" @click="openDirectModal">
            <font-awesome-icon icon="plus" />
          </button>
          <button class="btn-icon" title="New group" @click="openGroupModal">
            <font-awesome-icon icon="users" />
          </button>
        </div>
      </div>

      <div class="chat-sidebar__search">
        <input v-model="convoSearch" type="text" class="chat-search-input" placeholder="Search conversations..." />
      </div>

      <div class="chat-sidebar__list">
        <div v-if="loadingConvos" class="chat-loading">Loading…</div>
        <div
          v-for="c in filteredConvos"
          :key="c.id"
          class="chat-convo-item"
          :class="{ 'chat-convo-item--active': activeConvo?.id === c.id, 'chat-convo-item--unread': isUnread(c) && activeConvo?.id !== c.id }"
          @click="selectConvo(c)"
        >
          <div class="chat-avatar">{{ initials(c.name) }}</div>
          <div class="chat-convo-info">
            <div class="chat-convo-name">{{ c.name || 'Unknown' }}</div>
            <div class="chat-convo-preview">
              <span v-if="c.latest_message">
                <span v-if="c.latest_message.is_deleted" class="fst-italic text-muted">Message deleted</span>
                <span v-else>{{ c.latest_message.body ? stripHtml(c.latest_message.body).slice(0, 45) : '📎 File' }}</span>
              </span>
              <span v-else class="text-muted fst-italic">No messages yet</span>
            </div>
          </div>
          <div class="chat-convo-meta">
            <small v-if="c.last_message_at">{{ timeAgo(c.last_message_at) }}</small>
            <span v-if="isUnread(c)" class="chat-unread-dot"></span>
          </div>
        </div>
        <div v-if="!loadingConvos && filteredConvos.length === 0" class="chat-empty-state">
          No conversations yet. Start one!
        </div>
      </div>
    </div>

    <!-- ── Message panel ──────────────────────────────────────────────── -->
    <div class="chat-panel" :class="{ 'chat-panel--shown': mobileShowMessages }">
      <template v-if="activeConvo">
        <!-- Header -->
        <div class="chat-panel__header">
          <button class="btn-icon me-2 d-md-none" @click="mobileShowMessages = false">
            <font-awesome-icon icon="arrow-left" />
          </button>
          <div class="chat-avatar chat-avatar--sm">{{ initials(activeConvo.name) }}</div>
          <div class="ms-2">
            <div class="chat-panel-name">{{ activeConvo.name }}</div>
            <small v-if="typingUsers.length" class="text-muted fst-italic">{{ typingText }}</small>
            <small v-else-if="activeConvo.type === 'group'" class="text-muted">
              {{ activeConvo.participants?.length }} members
            </small>
          </div>
          <div class="ms-auto d-flex gap-2">
            <button v-if="activeConvo.type === 'group'" class="btn-icon" title="Members" @click="showMembers = !showMembers">
              <font-awesome-icon icon="users" />
            </button>
            <button v-if="activeConvo.type === 'group' && activeConvo.my_role === 'owner'" class="btn-icon" title="Add members" @click="openAddMembersModal">
              <font-awesome-icon icon="user-plus" />
            </button>
            <button v-if="activeConvo.type === 'group'" class="btn-icon text-danger" title="Leave group" @click="leaveGroup">
              <font-awesome-icon icon="sign-out-alt" />
            </button>
          </div>
        </div>

        <!-- Members panel (group) -->
        <div v-if="showMembers && activeConvo.type === 'group'" class="chat-members-panel">
          <div v-for="p in activeConvo.participants" :key="p.user_id" class="chat-member-item">
            <div class="chat-avatar chat-avatar--xs">{{ initials(p.name) }}</div>
            <span class="ms-2">{{ p.name }}</span>
            <span class="badge ms-auto" :class="roleBadge(p.role)">{{ p.role }}</span>
            <button
              v-if="activeConvo.my_role === 'owner' && p.user_id !== currentUserId"
              class="btn-icon text-danger ms-2"
              title="Remove"
              @click="removeMember(p.user_id)"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div class="chat-messages" ref="messagesEl" @scroll="onScroll">
          <div v-if="loadingMessages" class="chat-loading">Loading messages…</div>
          <div v-if="hasMore && !loadingMessages" class="text-center py-2">
            <button class="btn btn-sm btn-outline-secondary" @click="loadMore">Load earlier</button>
          </div>

          <template v-for="msg in messages" :key="msg.id">
            <div v-if="msg.id === firstUnreadId" class="chat-unread-divider">
              <span>New messages</span>
            </div>
            <div class="chat-msg-group" :class="msg.is_mine ? 'chat-msg-group--mine' : ''">
              <div class="chat-msg-sender" v-if="!msg.is_mine && activeConvo.type === 'group'">
                {{ msg.sender_name }}
              </div>
              <div class="chat-bubble" :class="msg.is_mine ? 'chat-bubble--mine' : 'chat-bubble--theirs'">
                <!-- Reply-to -->
                <div v-if="msg.reply_to" class="chat-reply-preview">
                  <font-awesome-icon icon="reply" class="me-1" />
                  {{ msg.reply_to.body ? stripHtml(msg.reply_to.body).slice(0, 60) : '📎 File' }}
                </div>

                <!-- Deleted -->
                <span v-if="msg.is_deleted" class="fst-italic text-muted small">Message deleted</span>

                <!-- File attachments -->
                <template v-else-if="msg.type === 'file' && msg.attachments?.length">
                  <div v-for="att in msg.attachments" :key="att.id" class="chat-attachment">
                    <!-- Audio / voice (server may store audio/webm or video/webm for MediaRecorder output) -->
                    <div v-if="isAudioAtt(att)" class="chat-audio-wrap">
                      <font-awesome-icon icon="microphone" class="chat-audio-icon" />
                      <audio v-if="attSrc(att)" :src="attSrc(att)" controls class="chat-audio-player" preload="none" />
                      <span v-else class="chat-audio-loading">Loading…</span>
                    </div>
                    <!-- Image -->
                    <img v-else-if="att.is_image || att.mime_type?.startsWith('image/')" :src="attSrc(att)" class="chat-img-preview" />
                    <!-- Other file -->
                    <div v-else class="chat-file">
                      <div class="chat-file-icon"><font-awesome-icon :icon="fileIcon(att.mime_type)" /></div>
                      <div class="chat-file-info">
                        <span class="chat-file-name">{{ att.file_name }}</span>
                        <span class="chat-file-size">{{ formatBytes(att.size_bytes) }}</span>
                      </div>
                      <button class="chat-file-dl" title="Download" @click="downloadFile(att)">
                        <font-awesome-icon icon="download" />
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Text -->
                <span v-else>{{ msg.body }}</span>

                <!-- Actions (own messages only) -->
                <div v-if="msg.is_mine && !msg.is_deleted" class="chat-msg-actions">
                  <button v-if="msg.type === 'text'" class="chat-action-btn" @click="startEdit(msg)">Edit</button>
                  <button class="chat-action-btn text-danger" @click="deleteMessage(msg)">Delete</button>
                  <button class="chat-action-btn" @click="replyingTo = msg">Reply</button>
                </div>
                <div v-else-if="!msg.is_mine && !msg.is_deleted" class="chat-msg-actions">
                  <button class="chat-action-btn" @click="replyingTo = msg">Reply</button>
                </div>
              </div>
              <div class="chat-msg-time">{{ formatTime(msg.created_at) }}<span v-if="msg.is_edited" class="ms-1">(edited)</span></div>
            </div>
          </template>
        </div>

        <!-- Reply preview -->
        <div v-if="replyingTo" class="chat-reply-bar">
          <font-awesome-icon icon="reply" class="me-2 text-primary" />
          <span class="text-muted small me-2">Replying to:</span>
          <span class="small">{{ replyingTo.body ? stripHtml(replyingTo.body).slice(0, 60) : '📎 File' }}</span>
          <button class="ms-auto btn-icon" @click="replyingTo = null"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Edit bar -->
        <div v-if="editingMessage" class="chat-reply-bar chat-reply-bar--edit">
          <font-awesome-icon icon="edit" class="me-2 text-warning" />
          <span class="small">Editing message</span>
          <button class="ms-auto btn-icon" @click="cancelEdit"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Input -->
        <div class="chat-input-area">
          <!-- Recording state -->
          <template v-if="isRecording">
            <div class="chat-recording">
              <span class="chat-rec-dot"></span>
              <span class="chat-rec-time">{{ formatRecTime(recordingSeconds) }}</span>
            </div>
            <button class="btn-icon chat-btn-cancel-rec" title="Cancel recording" @click="cancelRecording">
              <font-awesome-icon icon="times" />
            </button>
            <button class="chat-send-btn chat-send-btn--stop" title="Stop &amp; send" @click="stopRecording">
              <font-awesome-icon icon="stop" />
            </button>
          </template>

          <!-- Normal state -->
          <template v-else>
            <label class="btn-icon chat-attach-btn" title="Attach file or document (PDF, Word, Excel, images, video, ZIP… up to 20 MB)">
              <font-awesome-icon icon="paperclip" />
              <input type="file" class="d-none" ref="fileInput" @change="onFileSelected" />
            </label>

            <!-- Pending file chip -->
            <div v-if="pendingFile" class="chat-pending-file">
              <img v-if="pendingFileIsImage" :src="pendingFilePreviewUrl" class="chat-pending-img" alt="" />
              <font-awesome-icon v-else :icon="pendingFileIsAudio ? 'microphone' : 'file'" class="me-1" />
              <span class="chat-pending-name">{{ pendingFile.name }}</span>
              <button class="ms-1 btn-icon text-danger" @click="clearPendingFile"><font-awesome-icon icon="times" /></button>
            </div>

            <textarea
              v-model="newMessage"
              class="chat-textarea"
              :placeholder="pendingFile ? 'Add a caption (optional)…' : 'Type a message…'"
              rows="1"
              @keydown.enter.exact.prevent="sendMessage"
              @keydown.shift.enter.exact="newMessage += '\n'"
              @input="onInput"
            ></textarea>

            <!-- Send or mic -->
            <button v-if="newMessage.trim() || pendingFile" class="chat-send-btn" :disabled="sendingMessage" @click="sendMessage">
              <span v-if="sendingMessage" class="chat-send-spinner"></span>
              <font-awesome-icon v-else icon="paper-plane" />
            </button>
            <button v-else class="chat-send-btn chat-send-btn--mic" title="Voice message" @click="startRecording">
              <font-awesome-icon icon="microphone" />
            </button>
          </template>
        </div>
      </template>

      <!-- No conversation selected -->
      <div v-else class="chat-empty-panel">
        <font-awesome-icon icon="comments" class="chat-empty-icon" />
        <p>Select a conversation or start a new one</p>
      </div>
    </div>

    <!-- ── New direct message modal ──────────────────────────────────── -->
    <div v-if="showDirectModal" class="cmodal-overlay" @click.self="showDirectModal = false">
      <div class="cmodal-box cmodal-box--sm">
        <div class="cmodal-head">
          <div class="cmodal-head-left">
            <div class="cmodal-icon cmodal-icon--blue"><font-awesome-icon icon="comment-dots" /></div>
            <div>
              <div class="cmodal-title">New Message</div>
              <div class="cmodal-subtitle">Select an employee to chat with</div>
            </div>
          </div>
          <button class="cmodal-close" @click="showDirectModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="cmodal-body">
          <div class="cmodal-search">
            <font-awesome-icon icon="search" class="cmodal-search__icon" />
            <input v-model="employeeSearch" class="cmodal-search__input" type="text" placeholder="Search employees by name…" @input="onEmployeeSearch" />
          </div>
          <div class="cmodal-list">
            <div v-if="!employeeSearch.trim()" class="cmodal-empty">
              <font-awesome-icon icon="search" class="cmodal-empty__icon" />
              <span>Start typing to search</span>
            </div>
            <div v-else-if="loadingEmployees" class="cmodal-empty">
              <span class="cmodal-spinner"></span><span>Searching…</span>
            </div>
            <template v-else>
              <div v-for="emp in employees" :key="emp.id" class="cmodal-person" @click="startDirect(emp)">
                <div class="cmodal-person__avatar">{{ initials(emp.name) }}</div>
                <span class="cmodal-person__name">{{ emp.name }}</span>
                <font-awesome-icon icon="chevron-right" class="cmodal-person__arrow" />
              </div>
              <div v-if="employees.length === 0" class="cmodal-empty">
                <font-awesome-icon icon="user-slash" class="cmodal-empty__icon" />
                <span>No results for "{{ employeeSearch }}"</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Create group modal ────────────────────────────────────────── -->
    <div v-if="showGroupModal" class="cmodal-overlay" @click.self="showGroupModal = false">
      <div class="cmodal-box">
        <div class="cmodal-head">
          <div class="cmodal-head-left">
            <div class="cmodal-icon cmodal-icon--purple"><font-awesome-icon icon="users" /></div>
            <div>
              <div class="cmodal-title">Create Group Chat</div>
              <div class="cmodal-subtitle">Give it a name and add members</div>
            </div>
          </div>
          <button class="cmodal-close" @click="showGroupModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="cmodal-body">
          <div class="cmodal-field">
            <label class="cmodal-label">Group name <span class="cmodal-req">*</span></label>
            <input v-model="groupForm.name" class="cmodal-input" type="text" placeholder="e.g. Marketing Team" />
          </div>
          <div class="cmodal-field">
            <label class="cmodal-label">Description <span class="cmodal-opt">— optional</span></label>
            <input v-model="groupForm.description" class="cmodal-input" type="text" placeholder="What's this group about?" />
          </div>
          <div class="cmodal-field">
            <label class="cmodal-label">Members</label>
            <div class="cmodal-search">
              <font-awesome-icon icon="search" class="cmodal-search__icon" />
              <input v-model="employeeSearch" class="cmodal-search__input" type="text" placeholder="Search by name…" @input="onEmployeeSearch" />
            </div>
            <div v-if="groupForm.member_ids.length" class="cmodal-chips">
              <span v-for="id in groupForm.member_ids" :key="id" class="cmodal-chip">
                {{ selectedName(id) }}
                <button @click="groupForm.member_ids = groupForm.member_ids.filter(x => x !== id)">
                  <font-awesome-icon icon="times" />
                </button>
              </span>
            </div>
            <div class="cmodal-list">
              <div v-if="!employeeSearch.trim()" class="cmodal-empty">
                <font-awesome-icon icon="search" class="cmodal-empty__icon" />
                <span>Start typing to search</span>
              </div>
              <div v-else-if="loadingEmployees" class="cmodal-empty">
                <span class="cmodal-spinner"></span><span>Searching…</span>
              </div>
              <template v-else>
                <div
                  v-for="emp in employees" :key="emp.id"
                  class="cmodal-person"
                  :class="{ 'cmodal-person--selected': groupForm.member_ids.includes(emp.id) }"
                  @click="toggleGroupMember(emp)"
                >
                  <div class="cmodal-person__avatar">{{ initials(emp.name) }}</div>
                  <span class="cmodal-person__name">{{ emp.name }}</span>
                  <div class="cmodal-check" :class="{ 'cmodal-check--on': groupForm.member_ids.includes(emp.id) }">
                    <font-awesome-icon icon="check" />
                  </div>
                </div>
                <div v-if="employees.length === 0" class="cmodal-empty">
                  <font-awesome-icon icon="user-slash" class="cmodal-empty__icon" />
                  <span>No results found</span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="cmodal-footer">
          <button class="cmodal-btn cmodal-btn--ghost" @click="showGroupModal = false">Cancel</button>
          <button class="cmodal-btn cmodal-btn--primary" :disabled="creatingGroup" @click="createGroup">
            <span v-if="creatingGroup"><span class="cmodal-spinner cmodal-spinner--sm cmodal-spinner--white"></span> Creating…</span>
            <span v-else>Create Group</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Add members modal ─────────────────────────────────────────── -->
    <div v-if="showAddMembersModal" class="cmodal-overlay" @click.self="showAddMembersModal = false">
      <div class="cmodal-box cmodal-box--sm">
        <div class="cmodal-head">
          <div class="cmodal-head-left">
            <div class="cmodal-icon cmodal-icon--green"><font-awesome-icon icon="user-plus" /></div>
            <div>
              <div class="cmodal-title">Add Members</div>
              <div class="cmodal-subtitle">Select people to add to the group</div>
            </div>
          </div>
          <button class="cmodal-close" @click="showAddMembersModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="cmodal-body">
          <div class="cmodal-search">
            <font-awesome-icon icon="search" class="cmodal-search__icon" />
            <input v-model="employeeSearch" class="cmodal-search__input" type="text" placeholder="Search by name…" @input="onEmployeeSearch" />
          </div>
          <div class="cmodal-list">
            <div v-if="!employeeSearch.trim()" class="cmodal-empty">
              <font-awesome-icon icon="search" class="cmodal-empty__icon" />
              <span>Start typing to search</span>
            </div>
            <div v-else-if="loadingEmployees" class="cmodal-empty">
              <span class="cmodal-spinner"></span><span>Searching…</span>
            </div>
            <template v-else>
              <div
                v-for="emp in employees" :key="emp.id"
                class="cmodal-person"
                :class="{ 'cmodal-person--selected': addMemberIds.includes(emp.id) }"
                @click="toggleAddMember(emp)"
              >
                <div class="cmodal-person__avatar">{{ initials(emp.name) }}</div>
                <span class="cmodal-person__name">{{ emp.name }}</span>
                <div class="cmodal-check" :class="{ 'cmodal-check--on': addMemberIds.includes(emp.id) }">
                  <font-awesome-icon icon="check" />
                </div>
              </div>
              <div v-if="employees.length === 0" class="cmodal-empty">
                <font-awesome-icon icon="user-slash" class="cmodal-empty__icon" />
                <span>No results found</span>
              </div>
            </template>
          </div>
        </div>
        <div class="cmodal-footer">
          <button class="cmodal-btn cmodal-btn--ghost" @click="showAddMembersModal = false">Cancel</button>
          <button class="cmodal-btn cmodal-btn--primary" :disabled="addingMembers || addMemberIds.length === 0" @click="addMembers">
            <span v-if="addingMembers"><span class="cmodal-spinner cmodal-spinner--sm cmodal-spinner--white"></span> Adding…</span>
            <span v-else>Add{{ addMemberIds.length ? ` (${addMemberIds.length})` : '' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'HrChat',

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      conversations:     [],
      activeConvo:       null,
      messages:          [],
      newMessage:        '',
      pendingFile:       null,
      replyingTo:        null,
      editingMessage:    null,
      loadingConvos:     false,
      loadingMessages:   false,
      sendingMessage:    false,
      hasMore:           false,
      nextCursor:        null,
      convoSearch:       '',
      mobileShowMessages: false,
      showMembers:       false,

      // Modals
      showDirectModal:    false,
      showGroupModal:     false,
      showAddMembersModal: false,
      employees:          [],
      employeeSearch:     '',
      loadingEmployees:   false,
      employeeSearchTimer: null,
      groupForm:          { name: '', description: '', member_ids: [] },
      addMemberIds:       [],
      creatingGroup:      false,
      addingMembers:      false,

      // Unread tracking
      unreadSinceAt:     null,

      // Pending file preview
      pendingFilePreviewUrl: null,

      // Blob URLs for viewing/downloading attachments (keyed by attachment id)
      attachmentBlobs: {},

      // Voice recording
      isRecording:       false,
      recordingSeconds:  0,
      recordingTimer:    null,
      mediaRecorder:     null,
      recordingStream:   null,
      audioChunks:       [],

      // Typing
      typingUsers:       [],
      typingTimer:       null,

      // Echo channel
      echoChannel:       null,

      currentUserId: localStorage.getItem('user_id') || '',
    };
  },

  computed: {
    filteredConvos() {
      if (!this.convoSearch.trim()) return this.conversations;
      const q = this.convoSearch.toLowerCase();
      return this.conversations.filter(c => (c.name || '').toLowerCase().includes(q));
    },
    typingText() {
      if (!this.typingUsers.length) return '';
      if (this.typingUsers.length === 1) return `${this.typingUsers[0]} is typing…`;
      return `${this.typingUsers.slice(0, -1).join(', ')} and ${this.typingUsers.slice(-1)} are typing…`;
    },
    businessCode() {
      return this.$route?.params?.companyCode || localStorage.getItem('companyCode') || '';
    },
    pendingFileIsImage() {
      return !!(this.pendingFile && this.pendingFile.type.startsWith('image/'));
    },
    pendingFileIsAudio() {
      return !!(this.pendingFile && this.pendingFile.type.startsWith('audio/'));
    },
    firstUnreadId() {
      if (!this.unreadSinceAt) return null;
      const t = new Date(this.unreadSinceAt).getTime();
      const msg = this.messages.find(m => !m.is_mine && new Date(m.created_at).getTime() > t);
      return msg?.id ?? null;
    },
  },

  mounted() {
    this.fetchConversations();
  },

  beforeUnmount() {
    this.leaveChannel();
    if (this.isRecording) this.cancelRecording();
    if (this.pendingFilePreviewUrl) URL.revokeObjectURL(this.pendingFilePreviewUrl);
    this.revokeAllBlobs();
  },

  methods: {
    // ── Conversations ──────────────────────────────────────────────────
    async fetchConversations() {
      this.loadingConvos = true;
      try {
        const { data } = await axios.get('hr/chat/conversations');
        this.conversations = data.data || [];
      } catch {
        this.toast.error('Failed to load conversations');
      } finally {
        this.loadingConvos = false;
      }
    },

    async selectConvo(c) {
      if (this.activeConvo?.id === c.id) return;
      if (this.isRecording) this.cancelRecording();
      this.revokeAllBlobs();
      this.leaveChannel();
      // Capture last_read_at BEFORE fetch (fetch marks all as read server-side)
      this.unreadSinceAt = c.last_read_at || null;
      this.activeConvo = c;
      this.messages    = [];
      this.replyingTo  = null;
      this.editingMessage = null;
      this.showMembers = false;
      this.mobileShowMessages = true;
      await this.fetchMessages();
      // Mark conversation as locally read
      const idx = this.conversations.findIndex(x => x.id === c.id);
      if (idx !== -1) this.conversations[idx].last_read_at = new Date().toISOString();
      this.joinChannel(c.id);
    },

    // ── Messages ───────────────────────────────────────────────────────
    async fetchMessages(before = null) {
      if (!this.activeConvo) return;
      this.loadingMessages = true;
      try {
        const { data } = await axios.get(`hr/chat/conversations/${this.activeConvo.id}/messages`, {
          params: { before, limit: 50 },
        });
        const result = data.data || {};
        const incoming = result.data || [];
        if (before) {
          this.messages = [...incoming, ...this.messages];
          this.prefetchAttachments(incoming);
        } else {
          this.messages = incoming;
          this.prefetchAttachments(incoming);
          this.$nextTick(() => {
            const divider = this.$el?.querySelector('.chat-unread-divider');
            if (divider) {
              divider.scrollIntoView({ block: 'center' });
            } else {
              this.scrollToBottom();
            }
          });
        }
        this.hasMore   = result.has_more  || false;
        this.nextCursor = result.next_cursor || null;
      } catch {
        this.toast.error('Failed to load messages');
      } finally {
        this.loadingMessages = false;
      }
    },

    async loadMore() {
      await this.fetchMessages(this.nextCursor);
    },

    async sendMessage() {
      if (!this.newMessage.trim() && !this.pendingFile) return;
      if (this.sendingMessage) return;

      if (this.editingMessage) {
        await this.submitEdit();
        return;
      }

      this.sendingMessage = true;
      try {
        const form = new FormData();
        if (this.newMessage.trim()) form.append('body', this.newMessage.trim());
        if (this.pendingFile)       form.append('file', this.pendingFile);
        if (this.replyingTo)        form.append('reply_to_id', this.replyingTo.id);

        // Let axios set Content-Type automatically — manual override strips the multipart boundary
        const { data } = await axios.post(
          `hr/chat/conversations/${this.activeConvo.id}/messages`,
          form
        );

        this.messages.push(data.data);
        this.prefetchAttachments([data.data]);
        this.newMessage  = '';
        this.clearPendingFile();
        this.replyingTo  = null;
        this.$nextTick(() => this.scrollToBottom());

        // Update convo preview
        const c = this.conversations.find(c => c.id === this.activeConvo.id);
        if (c) {
          c.latest_message  = data.data;
          c.last_message_at = data.data.created_at;
        }
        this.conversations.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      } catch {
        this.toast.error('Failed to send message');
      } finally {
        this.sendingMessage = false;
      }
    },

    startEdit(msg) {
      this.editingMessage = msg;
      this.newMessage     = msg.body || '';
    },

    cancelEdit() {
      this.editingMessage = null;
      this.newMessage     = '';
    },

    async submitEdit() {
      try {
        const { data } = await axios.put(
          `hr/chat/conversations/${this.activeConvo.id}/messages/${this.editingMessage.id}`,
          { body: this.newMessage.trim() }
        );
        const idx = this.messages.findIndex(m => m.id === this.editingMessage.id);
        if (idx !== -1) this.messages[idx] = data.data;
        this.editingMessage = null;
        this.newMessage     = '';
      } catch {
        this.toast.error('Failed to edit message');
      }
    },

    async deleteMessage(msg) {
      if (!confirm('Delete this message?')) return;
      try {
        await axios.delete(`hr/chat/conversations/${this.activeConvo.id}/messages/${msg.id}`);
        const m = this.messages.find(m => m.id === msg.id);
        if (m) { m.is_deleted = true; m.body = null; m.attachments = []; }
      } catch {
        this.toast.error('Failed to delete message');
      }
    },

    // ── Realtime (Echo) ────────────────────────────────────────────────
    joinChannel(conversationId) {
      if (!window.Echo) return;
      const channelName = `chat.${this.businessCode}.${conversationId}`;
      this.echoChannel  = window.Echo.private(channelName);

      this.echoChannel.listen('.message.new', (e) => {
        if (e.sender_id === this.currentUserId) return; // already pushed locally
        const incoming = { ...e, is_mine: false };
        this.messages.push(incoming);
        this.prefetchAttachments([incoming]);
        this.$nextTick(() => this.scrollToBottom());
        const c = this.conversations.find(c => c.id === conversationId);
        if (c) {
          c.latest_message  = e;
          c.last_message_at = e.created_at;
          // User is actively viewing — mark as read immediately
          c.last_read_at = new Date().toISOString();
        }
        this.conversations.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      });

      this.echoChannel.listen('.message.edited', (e) => {
        const idx = this.messages.findIndex(m => m.id === e.id);
        if (idx !== -1) Object.assign(this.messages[idx], { body: e.body, is_edited: true, edited_at: e.edited_at });
      });

      this.echoChannel.listen('.message.deleted', (e) => {
        const m = this.messages.find(m => m.id === e.id);
        if (m) { m.is_deleted = true; m.body = null; m.attachments = []; }
      });

      this.echoChannel.listen('.typing', (e) => {
        if (e.user_id === this.currentUserId) return;
        if (e.is_typing) {
          if (!this.typingUsers.includes(e.user_name)) this.typingUsers.push(e.user_name);
        } else {
          this.typingUsers = this.typingUsers.filter(n => n !== e.user_name);
        }
      });
    },

    leaveChannel() {
      if (this.echoChannel && window.Echo) {
        window.Echo.leave(this.echoChannel.name);
        this.echoChannel = null;
        this.typingUsers = [];
      }
    },

    // ── Typing indicator ───────────────────────────────────────────────
    onInput() {
      if (!this.activeConvo) return;
      if (this.typingTimer) clearTimeout(this.typingTimer);
      axios.post(`hr/chat/conversations/${this.activeConvo.id}/typing`, { is_typing: true }).catch(() => {});
      this.typingTimer = setTimeout(() => {
        axios.post(`hr/chat/conversations/${this.activeConvo.id}/typing`, { is_typing: false }).catch(() => {});
      }, 2500);
    },

    // ── Direct / Group ─────────────────────────────────────────────────
    openDirectModal() {
      this.showDirectModal = true;
      this.employeeSearch  = '';
      this.employees       = [];
    },

    async startDirect(emp) {
      this.showDirectModal = false;
      try {
        const { data } = await axios.post('hr/chat/conversations/direct', { user_id: emp.id });
        const existing = this.conversations.find(c => c.id === data.data.id);
        if (!existing) this.conversations.unshift(data.data);
        await this.selectConvo(data.data);
      } catch {
        this.toast.error('Failed to start conversation');
      }
    },

    openGroupModal() {
      this.showGroupModal = true;
      this.groupForm      = { name: '', description: '', member_ids: [] };
      this.employeeSearch = '';
      this.employees      = [];
    },

    toggleGroupMember(emp) {
      const idx = this.groupForm.member_ids.indexOf(emp.id);
      if (idx === -1) this.groupForm.member_ids.push(emp.id);
      else            this.groupForm.member_ids.splice(idx, 1);
    },

    async createGroup() {
      if (!this.groupForm.name.trim()) { this.toast.error('Group name is required'); return; }
      this.creatingGroup = true;
      try {
        const { data } = await axios.post('hr/chat/conversations/group', this.groupForm);
        this.conversations.unshift(data.data);
        this.showGroupModal = false;
        await this.selectConvo(data.data);
      } catch {
        this.toast.error('Failed to create group');
      } finally {
        this.creatingGroup = false;
      }
    },

    openAddMembersModal() {
      this.showAddMembersModal = true;
      this.addMemberIds        = [];
      this.employeeSearch      = '';
      this.employees           = [];
    },

    toggleAddMember(emp) {
      const idx = this.addMemberIds.indexOf(emp.id);
      if (idx === -1) this.addMemberIds.push(emp.id);
      else            this.addMemberIds.splice(idx, 1);
    },

    async addMembers() {
      this.addingMembers = true;
      try {
        await axios.post(`hr/chat/conversations/${this.activeConvo.id}/members`, { member_ids: this.addMemberIds });
        this.showAddMembersModal = false;
        this.toast.success('Members added');
        await this.fetchConversations();
        const updated = this.conversations.find(c => c.id === this.activeConvo.id);
        if (updated) this.activeConvo = updated;
      } catch {
        this.toast.error('Failed to add members');
      } finally {
        this.addingMembers = false;
      }
    },

    async removeMember(userId) {
      if (!confirm('Remove this member?')) return;
      try {
        await axios.delete(`hr/chat/conversations/${this.activeConvo.id}/members/${userId}`);
        if (this.activeConvo.participants) {
          this.activeConvo.participants = this.activeConvo.participants.filter(p => p.user_id !== userId);
        }
        this.toast.success('Member removed');
      } catch {
        this.toast.error('Failed to remove member');
      }
    },

    async leaveGroup() {
      if (!confirm('Leave this group?')) return;
      try {
        await axios.post(`hr/chat/conversations/${this.activeConvo.id}/leave`);
        this.conversations = this.conversations.filter(c => c.id !== this.activeConvo.id);
        this.activeConvo   = null;
        this.leaveChannel();
        this.mobileShowMessages = false;
      } catch {
        this.toast.error('Failed to leave group');
      }
    },

    // ── Employees ─────────────────────────────────────────────────────
    onEmployeeSearch() {
      if (!this.employeeSearch.trim()) {
        this.employees = [];
        return;
      }
      clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => this.fetchEmployees(), 300);
    },

    async fetchEmployees() {
      if (!this.employeeSearch.trim()) return;
      this.loadingEmployees = true;
      try {
        const { data } = await axios.get('hr/chat/employees', { params: { search: this.employeeSearch } });
        this.employees = data.data || [];
      } catch { /* silent */ } finally {
        this.loadingEmployees = false;
      }
    },

    // ── File ──────────────────────────────────────────────────────────
    onFileSelected(e) {
      const file = e.target.files[0] || null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      if (file) this.setPendingFile(file);
    },

    setPendingFile(file) {
      if (this.pendingFilePreviewUrl) URL.revokeObjectURL(this.pendingFilePreviewUrl);
      this.pendingFile = file;
      this.pendingFilePreviewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : null;
    },

    clearPendingFile() {
      if (this.pendingFilePreviewUrl) {
        URL.revokeObjectURL(this.pendingFilePreviewUrl);
        this.pendingFilePreviewUrl = null;
      }
      this.pendingFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    // ── Voice recording ───────────────────────────────────────────────
    async startRecording() {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.toast.error('Voice recording is not supported in this browser');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.recordingStream = stream;
        this.audioChunks     = [];
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
                       : MediaRecorder.isTypeSupported('audio/ogg')  ? 'audio/ogg'
                       : '';
        this.mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        this.mediaRecorder.addEventListener('dataavailable', e => {
          if (e.data.size > 0) this.audioChunks.push(e.data);
        });
        this.mediaRecorder.start();
        this.isRecording      = true;
        this.recordingSeconds = 0;
        this.recordingTimer   = setInterval(() => {
          this.recordingSeconds++;
          if (this.recordingSeconds >= 120) this.stopRecording(); // 2 min max
        }, 1000);
      } catch {
        this.toast.error('Microphone access denied');
      }
    },

    stopRecording() {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return;
      this.mediaRecorder.addEventListener('stop', () => {
        const mimeType = this.mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(this.audioChunks, { type: mimeType });
        const ext  = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mimeType });
        this.setPendingFile(file);
        this.stopAudioTracks();
        this.audioChunks = [];
      }, { once: true });
      this.mediaRecorder.stop();
      clearInterval(this.recordingTimer);
      this.isRecording = false;
    },

    cancelRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      clearInterval(this.recordingTimer);
      this.isRecording      = false;
      this.recordingSeconds = 0;
      this.audioChunks      = [];
      this.stopAudioTracks();
    },

    stopAudioTracks() {
      if (this.recordingStream) {
        this.recordingStream.getTracks().forEach(t => t.stop());
        this.recordingStream = null;
      }
    },

    formatRecTime(secs) {
      const m = String(Math.floor(secs / 60)).padStart(2, '0');
      const s = String(secs % 60).padStart(2, '0');
      return `${m}:${s}`;
    },

    // ── Attachment display / download ─────────────────────────────────
    // S3 temp_url is embedded in the message — use it directly.
    // Fallback: fetch a fresh URL from the /url endpoint (handles expiry).
    attSrc(att) {
      return att.temp_url || this.attachmentBlobs[att.id] || null;
    },

    isAudioAtt(att) {
      const m = att.mime_type || '';
      return m.startsWith('audio/') || m === 'video/webm';
    },

    prefetchAttachments(msgs) {
      // Only needed when temp_url is absent (local-disk dev mode)
      msgs.forEach(msg => {
        if (msg.type !== 'file' || !msg.attachments?.length) return;
        msg.attachments.forEach(att => {
          if (!att.temp_url && !this.attachmentBlobs[att.id]) {
            this.fetchAttachmentUrl(att);
          }
        });
      });
    },

    async fetchAttachmentUrl(att) {
      try {
        const { data } = await axios.get(`hr/chat/attachments/${att.id}/url`);
        if (data.data?.url) this.attachmentBlobs[att.id] = data.data.url;
      } catch { /* silent */ }
    },

    async downloadFile(att) {
      const url = att.download_url || att.temp_url;
      if (!url) { this.toast.error('File not available'); return; }
      try {
        // Fetch the file directly from S3 (pre-signed URL — no auth header needed).
        // S3 CORS is configured to allow cross-origin GET, so this works from the browser.
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.file_name;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 15000);
      } catch (e) {
        console.error('Download failed:', e);
        this.toast.error('Failed to download file');
      }
    },

    revokeAllBlobs() {
      Object.values(this.attachmentBlobs).forEach(url => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
      this.attachmentBlobs = {};
    },

    fileIcon(mimeType) {
      if (!mimeType) return 'file';
      if (mimeType.includes('pdf'))        return 'file-pdf';
      if (mimeType.includes('word') || mimeType.includes('document')) return 'file-word';
      if (mimeType.includes('excel') || mimeType.includes('sheet'))   return 'file-excel';
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'file-powerpoint';
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'file-archive';
      if (mimeType.startsWith('text/'))    return 'file-alt';
      if (mimeType.startsWith('video/'))   return 'file-video';
      return 'file';
    },

    attachmentDownloadUrl(id) {
      const code = this.businessCode;
      return `http://127.0.0.1:8000/api/${code}/hr/chat/attachments/${id}/download`;
    },

    attachmentPreviewUrl(id) {
      return this.attachmentDownloadUrl(id);
    },

    // ── Scroll ────────────────────────────────────────────────────────
    scrollToBottom() {
      const el = this.$refs.messagesEl;
      if (el) el.scrollTop = el.scrollHeight;
    },

    onScroll() { /* load-more handled by button for now */ },

    // ── Helpers ───────────────────────────────────────────────────────
    isUnread(c) {
      if (!c.last_message_at) return false;
      if (!c.last_read_at) return true;
      return new Date(c.last_message_at).getTime() > new Date(c.last_read_at).getTime();
    },

    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
    },

    stripHtml(html) {
      return (html || '').replace(/<[^>]*>/g, '').trim();
    },

    timeAgo(dt) {
      if (!dt) return '';
      const d = new Date(dt);
      const now = new Date();
      const secs = Math.floor((now - d) / 1000);
      if (secs < 60)   return 'just now';
      if (secs < 3600) return `${Math.floor(secs / 60)}m`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
      return d.toLocaleDateString();
    },

    formatTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    formatBytes(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1048576).toFixed(1)} MB`;
    },

    roleBadge(role) {
      return { 'bg-primary': role === 'owner', 'bg-secondary': role !== 'owner' };
    },

    selectedName(id) {
      const found = this.employees.find(e => e.id === id);
      return found ? found.name : '…';
    },
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────── */
.chat-page {
  display: flex;
  height: calc(100vh - 64px);
  background: #f4f6fb;
  overflow: hidden;
}

/* ── Sidebar ─────────────────────────────────────────────────────────── */
.chat-sidebar {
  width: 300px;
  min-width: 300px;
  background: #fff;
  border-right: 1px solid #e4eaf2;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.chat-sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e4eaf2;
}
.chat-sidebar__search { padding: 8px 12px; border-bottom: 1px solid #f0f2f5; }
.chat-search-input {
  width: 100%; padding: 6px 10px; border: 1px solid #dde5f0;
  border-radius: 20px; font-size: 13px; outline: none;
  background: #f7f9fc;
}
.chat-sidebar__list { overflow-y: auto; flex: 1; }

.chat-convo-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; cursor: pointer;
  border-bottom: 1px solid #f5f7fb;
  transition: background .12s;
}
.chat-convo-item:hover { background: #f4f6fb; }
.chat-convo-item--active { background: #eef2ff; }
.chat-convo-item--unread { background: #f0f7ff; }
.chat-convo-item--unread:hover { background: #e6f0fd; }

.chat-convo-info { flex: 1; min-width: 0; }
.chat-convo-name { font-size: 13.5px; font-weight: 600; color: #1a2b45; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-convo-item--unread .chat-convo-name { font-weight: 700; color: #0f172a; }
.chat-convo-item--unread .chat-convo-preview { color: #475569; font-weight: 500; }
.chat-convo-preview { font-size: 12px; color: #8994a8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.chat-convo-meta { font-size: 11px; color: #b0bac9; white-space: nowrap; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }

/* Unread dot in sidebar */
.chat-unread-dot { width: 9px; height: 9px; border-radius: 50%; background: #004AAD; flex-shrink: 0; }

/* Unread messages divider */
.chat-unread-divider {
  display: flex; align-items: center; gap: 10px;
  align-self: stretch; padding: 4px 0; margin: 4px 0;
  color: #2563eb; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .5px;
}
.chat-unread-divider::before,
.chat-unread-divider::after { content: ''; flex: 1; height: 1px; background: #bfdbfe; }

/* ── Panel ───────────────────────────────────────────────────────────── */
.chat-panel {
  flex: 1; display: flex; flex-direction: column;
  min-width: 0; background: #f9fafc;
}
.chat-panel__header {
  display: flex; align-items: center;
  padding: 12px 16px; background: #fff;
  border-bottom: 1px solid #e4eaf2;
  gap: 6px; flex-shrink: 0;
}
.chat-panel-name { font-weight: 600; font-size: 14px; color: #1a2b45; }

/* ── Members panel ───────────────────────────────────────────────────── */
.chat-members-panel {
  background: #fff; border-bottom: 1px solid #e4eaf2;
  max-height: 200px; overflow-y: auto; padding: 8px 12px;
}
.chat-member-item { display: flex; align-items: center; padding: 5px 0; font-size: 13px; }

/* ── Messages ────────────────────────────────────────────────────────── */
.chat-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 4px; }

.chat-msg-group { display: flex; flex-direction: column; max-width: 70%; }
.chat-msg-group--mine { align-self: flex-end; align-items: flex-end; }
.chat-msg-sender { font-size: 11px; color: #8994a8; margin-bottom: 2px; padding-left: 4px; }
.chat-msg-time { font-size: 10px; color: #b0bac9; margin-top: 2px; padding: 0 4px; }

.chat-bubble {
  padding: 8px 12px; border-radius: 14px; font-size: 13.5px;
  line-height: 1.5; position: relative; word-break: break-word;
}
.chat-bubble--mine   { background: #004AAD; color: #fff; border-bottom-right-radius: 4px; }
.chat-bubble--theirs { background: #fff; color: #1a2b45; border-bottom-left-radius: 4px; box-shadow: 0 1px 3px rgba(0,0,0,.06); }

/* ── Bubble hover actions ────────────────────────────────────────────── */
.chat-bubble { position: relative; }
.chat-msg-actions { display: none; position: absolute; top: -22px; right: 0; background: #fff; border: 1px solid #e4eaf2; border-radius: 6px; padding: 2px 6px; gap: 6px; font-size: 11px; box-shadow: 0 2px 6px rgba(0,0,0,.08); }
.chat-bubble:hover .chat-msg-actions { display: flex; }
.chat-action-btn { background: none; border: none; cursor: pointer; font-size: 11px; padding: 0 2px; color: #4a5568; }
.chat-action-btn:hover { color: #004AAD; }

/* ── Reply preview inside bubble ─────────────────────────────────────── */
.chat-reply-preview { border-left: 3px solid rgba(255,255,255,.5); padding-left: 8px; margin-bottom: 4px; font-size: 11.5px; opacity: .8; }
.chat-bubble--theirs .chat-reply-preview { border-color: #004AAD; }

/* ── Attachments ─────────────────────────────────────────────────────── */
.chat-img-preview { max-width: 220px; max-height: 180px; border-radius: 8px; display: block; }
.chat-file { display: flex; align-items: center; font-size: 13px; }
.chat-file-link { color: inherit; text-decoration: underline; }

/* ── Reply bar / edit bar ────────────────────────────────────────────── */
.chat-reply-bar {
  display: flex; align-items: center; padding: 6px 14px;
  background: #eef2ff; border-top: 1px solid #dde5f0; font-size: 12.5px;
}
.chat-reply-bar--edit { background: #fff8e5; }

/* ── Input ───────────────────────────────────────────────────────────── */
.chat-input-area {
  display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 14px; background: #fff;
  border-top: 1px solid #e4eaf2; flex-shrink: 0;
}
.chat-textarea {
  flex: 1; resize: none; border: 1px solid #dde5f0; border-radius: 20px;
  padding: 8px 14px; font-size: 13.5px; outline: none; max-height: 120px;
  overflow-y: auto; background: #f7f9fc; line-height: 1.4;
}
.chat-textarea:focus { border-color: #004AAD; background: #fff; }
.chat-send-btn {
  background: #004AAD; color: #fff; border: none;
  width: 36px; height: 36px; border-radius: 50%; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .15s;
}
.chat-send-btn:disabled { opacity: .6; cursor: not-allowed; }
.chat-send-btn:hover:not(:disabled) { background: #003a8a; }

.chat-pending-file {
  font-size: 12px; background: #eef2ff; padding: 3px 8px; border-radius: 12px;
  display: flex; align-items: center; gap: 5px; max-width: 180px;
  border: 1px solid #c7d7fb; flex-shrink: 0;
}
.chat-pending-img  { width: 36px; height: 36px; object-fit: cover; border-radius: 5px; flex-shrink: 0; }
.chat-pending-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }

/* Attach button — slightly stronger than generic btn-icon so it reads as actionable */
.chat-attach-btn { color: #475569; font-size: 16px; }
.chat-attach-btn:hover { color: #004AAD; background: #eef2ff; }

/* ── Mic / send variants ─────────────────────────────────────────────── */
.chat-send-btn--mic  { background: #f1f5f9; color: #475569; box-shadow: none; }
.chat-send-btn--mic:hover { background: #e2e8f0; color: #004AAD; }
.chat-send-btn--stop { background: #dc2626; }
.chat-send-btn--stop:hover { background: #b91c1c; }
.chat-send-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Recording bar ───────────────────────────────────────────────────── */
.chat-recording {
  flex: 1; display: flex; align-items: center; gap: 10px;
  background: #fff0f0; border: 1px solid #fecaca;
  border-radius: 20px; padding: 7px 14px; min-width: 0;
}
.chat-rec-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: #dc2626; flex-shrink: 0;
  animation: rec-pulse 1s ease-in-out infinite;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: .45; transform: scale(.75); }
}
.chat-rec-time { font-size: 13.5px; font-weight: 700; color: #dc2626; font-variant-numeric: tabular-nums; flex: 1; }
.chat-btn-cancel-rec { color: #dc2626; }
.chat-btn-cancel-rec:hover { background: #fff0f0; }

/* ── Audio attachment ────────────────────────────────────────────────── */
.chat-audio-wrap { display: flex; align-items: center; gap: 8px; }
.chat-audio-icon { font-size: 13px; opacity: .7; flex-shrink: 0; }
.chat-audio-player { width: 200px; height: 32px; accent-color: #004AAD; }
.chat-bubble--mine .chat-audio-player { accent-color: #fff; }

/* ── Document file bubble ────────────────────────────────────────────── */
.chat-file {
  display: flex; align-items: center; gap: 10px;
  background: rgba(0,0,0,.06); border-radius: 8px; padding: 8px 10px;
  min-width: 180px; max-width: 240px;
}
.chat-bubble--mine .chat-file { background: rgba(255,255,255,.15); }
.chat-file-icon {
  width: 32px; height: 32px; border-radius: 6px; background: rgba(0,0,0,.08);
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; flex-shrink: 0;
}
.chat-bubble--mine .chat-file-icon { background: rgba(255,255,255,.2); }
.chat-file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.chat-file-name { font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chat-file-size { font-size: 10.5px; opacity: .65; }
.chat-file-dl {
  width: 28px; height: 28px; border-radius: 6px; background: rgba(0,0,0,.08);
  display: flex; align-items: center; justify-content: center;
  color: inherit; font-size: 12px; text-decoration: none; flex-shrink: 0;
  transition: background .12s;
}
.chat-file-dl:hover { background: rgba(0,0,0,.15); }

/* ── Avatar ──────────────────────────────────────────────────────────── */
.chat-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: #004AAD; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; flex-shrink: 0;
}
.chat-avatar--sm { width: 32px; height: 32px; font-size: 11px; }
.chat-avatar--xs { width: 26px; height: 26px; font-size: 10px; }

/* ── Empty / loading states ──────────────────────────────────────────── */
.chat-loading    { text-align: center; color: #8994a8; padding: 24px; font-size: 13px; }
.chat-empty-state { text-align: center; color: #b0bac9; padding: 32px 16px; font-size: 13px; }
.chat-empty-panel { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; color: #b0bac9; gap: 12px; }
.chat-empty-icon { font-size: 48px; opacity: .4; }

/* ── Icon buttons ────────────────────────────────────────────────────── */
.btn-icon { background: none; border: none; cursor: pointer; color: #6b7a99; padding: 4px 6px; border-radius: 6px; display: inline-flex; align-items: center; transition: color .15s, background .15s; }
.btn-icon:hover { color: #004AAD; background: #f0f4ff; }

/* ── Mobile ──────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .chat-sidebar { width: 100%; min-width: 0; position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1; }
  .chat-sidebar--hidden { display: none; }
  .chat-panel { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 2; display: none; }
  .chat-panel--shown { display: flex; }
}

/* ── Modals ──────────────────────────────────────────────────────────── */
.cmodal-overlay {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(15,23,42,.6);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.cmodal-box {
  background: #fff; border-radius: 16px; width: 100%; max-width: 500px;
  max-height: 88vh; display: flex; flex-direction: column;
  box-shadow: 0 32px 80px rgba(0,0,0,.24), 0 8px 24px rgba(0,0,0,.12);
  overflow: hidden;
}
.cmodal-box--sm { max-width: 420px; }

.cmodal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 22px 18px; border-bottom: 1px solid #f1f5f9;
}
.cmodal-head-left { display: flex; align-items: center; gap: 14px; }
.cmodal-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: #fff;
}
.cmodal-icon--blue   { background: linear-gradient(135deg,#004AAD,#2563eb); }
.cmodal-icon--purple { background: linear-gradient(135deg,#7c3aed,#a855f7); }
.cmodal-icon--green  { background: linear-gradient(135deg,#059669,#10b981); }
.cmodal-title    { font-size: 15px; font-weight: 700; color: #0f172a; }
.cmodal-subtitle { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.cmodal-close {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  width: 34px; height: 34px; cursor: pointer; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s; font-size: 13px; flex-shrink: 0;
}
.cmodal-close:hover { background: #fee2e2; border-color: #fca5a5; color: #dc2626; }

.cmodal-body { padding: 18px 22px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 16px; }

.cmodal-field { display: flex; flex-direction: column; gap: 6px; }
.cmodal-label { font-size: 12px; font-weight: 700; color: #475569; letter-spacing: .3px; text-transform: uppercase; }
.cmodal-req   { color: #ef4444; }
.cmodal-opt   { font-weight: 400; color: #94a3b8; text-transform: none; letter-spacing: 0; }
.cmodal-input {
  width: 100%; padding: 9px 12px; border: 1.5px solid #e2e8f0;
  border-radius: 10px; font-size: 13.5px; color: #0f172a;
  background: #f8fafc; outline: none; transition: all .15s;
}
.cmodal-input:focus { border-color: #004AAD; background: #fff; box-shadow: 0 0 0 3px rgba(0,74,173,.1); }

.cmodal-search {
  position: relative; display: flex; align-items: center;
  border: 1.5px solid #e2e8f0; border-radius: 10px; background: #f8fafc;
  transition: all .15s;
}
.cmodal-search:focus-within { border-color: #004AAD; background: #fff; box-shadow: 0 0 0 3px rgba(0,74,173,.1); }
.cmodal-search__icon  { position: absolute; left: 12px; color: #94a3b8; font-size: 13px; pointer-events: none; }
.cmodal-search__input { width: 100%; padding: 9px 12px 9px 36px; border: none; background: transparent; font-size: 13.5px; color: #0f172a; outline: none; }

.cmodal-list {
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  overflow: hidden; max-height: 230px; overflow-y: auto;
}
.cmodal-person {
  display: flex; align-items: center; gap: 11px;
  padding: 10px 14px; cursor: pointer; transition: background .12s;
  border-bottom: 1px solid #f1f5f9;
}
.cmodal-person:last-child { border-bottom: none; }
.cmodal-person:hover { background: #f0f7ff; }
.cmodal-person--selected { background: #eff6ff; }
.cmodal-person__avatar {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg,#004AAD,#2563eb); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
}
.cmodal-person__name  { flex: 1; font-size: 13.5px; font-weight: 500; color: #0f172a; }
.cmodal-person__arrow { color: #cbd5e1; font-size: 11px; }
.cmodal-check {
  width: 22px; height: 22px; border-radius: 50%; border: 2px solid #cbd5e1;
  display: flex; align-items: center; justify-content: center;
  color: transparent; font-size: 9px; flex-shrink: 0; transition: all .15s;
}
.cmodal-check--on { background: #004AAD; border-color: #004AAD; color: #fff; }

.cmodal-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.cmodal-chip {
  display: inline-flex; align-items: center; gap: 5px;
  background: #eff6ff; color: #004AAD; border: 1px solid #bfdbfe;
  border-radius: 20px; padding: 3px 8px 3px 10px; font-size: 12px; font-weight: 600;
}
.cmodal-chip button {
  background: none; border: none; cursor: pointer; padding: 0; line-height: 1;
  color: #93c5fd; font-size: 10px; display: flex; align-items: center; transition: color .15s;
}
.cmodal-chip button:hover { color: #dc2626; }

.cmodal-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; padding: 24px 12px; color: #94a3b8; font-size: 13px;
}
.cmodal-empty__icon { font-size: 22px; opacity: .5; }

.cmodal-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid #e2e8f0; border-top-color: #004AAD;
  border-radius: 50%; animation: cmodal-spin .65s linear infinite; flex-shrink: 0;
}
.cmodal-spinner--sm { width: 12px; height: 12px; }
.cmodal-spinner--white { border-color: rgba(255,255,255,.25); border-top-color: #fff; }
@keyframes cmodal-spin { to { transform: rotate(360deg); } }

.cmodal-footer {
  display: flex; justify-content: flex-end; align-items: center; gap: 10px;
  padding: 14px 22px; border-top: 1px solid #f1f5f9;
}
.cmodal-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 20px; border-radius: 10px; font-size: 13.5px;
  font-weight: 600; border: none; cursor: pointer; transition: all .15s;
}
.cmodal-btn--ghost   { background: #f1f5f9; color: #475569; }
.cmodal-btn--ghost:hover { background: #e2e8f0; }
.cmodal-btn--primary { background: #004AAD; color: #fff; box-shadow: 0 2px 8px rgba(0,74,173,.3); }
.cmodal-btn--primary:hover:not(:disabled) { background: #0041a3; box-shadow: 0 4px 12px rgba(0,74,173,.4); }
.cmodal-btn--primary:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
</style>
